import { type FC, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { type Swiper as SwiperInstance } from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import ChanceDrop25 from '@/assets/img/icons/chance-drop-25.svg?react';
import { type ArenaOffersDto } from '@/common/types/shop/marketBoxes';
import { ModalContext } from '@/components/Modals/Modal/config';
import { H2, StringSmall } from '@/components/Typography/Typography';
import { GreyBlurButton, MainButton } from '@/components/ui/Button/Button';
import { MotionPicture, Picture } from '@/components/ui/Picture/Picture';

import ProfitDropRewardCardsList from '../Rewards/ProfitDropRewardCardsList';

import lightGreenPng from '/img/light-cyan.png';
import lightGreenWebp from '/img/light-cyan.webp';
import usdtCoinsPng from '/img/shop/arenaOffers/6and15usdt.png';
import usdtCoinsWebp from '/img/shop/arenaOffers/6and15usdt.webp';

interface ProfitDropInfoTabProps {
  activeCard: ArenaOffersDto;
}

const MotionGreyButton = motion.create(GreyBlurButton);
const MotionMainButton = motion.create(MainButton);

const ProfitDropInfoTab: FC<ProfitDropInfoTabProps> = ({ activeCard }) => {
  const { t } = useTranslation();
  const swiperRef = useRef<SwiperInstance>();
  const [activeSlide, setActiveSlide] = useState(0);
  const { openModal } = useContext(ModalContext);

  const onBuyOffer = (): void => {
    openModal({
      renderKind: 'buyArenaOffer',
      props: {
        arenaOffer: activeCard,
      },
    });
  };

  return (
    <>
      <MotionPicture
        img={lightGreenPng}
        webp={lightGreenWebp}
        alt="background light"
        animate={{ opacity: activeSlide === 2 ? 1 : 0 }}
        initial={{ opacity: 0 }}
        transition={{
          duration: 0.2,
          delay: 0.05,
        }}
        className="absolute -top-2 w-[21.25rem] left-1/2 -translate-x-1/2"
      />
      <div>
        <Swiper
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          modules={[Navigation, Pagination]}
          navigation={true}
          pagination={{ clickable: true }}
          slidesPerView={1}
          spaceBetween={10}
          centeredSlides={true}
          loop={false}
          onSlideChange={(slide) => setActiveSlide(slide.activeIndex)}
          allowTouchMove={true}
        >
          <SwiperSlide>
            <div className="flex flex-col gap-4 items-center pb-8 pt-6">
              <ProfitDropRewardCardsList rewards={activeCard.reward} />
              <StringSmall className="max-w-[220px] text-center">
                {t('Player receives one of the package rewards upon purchase.')}
              </StringSmall>
            </div>
          </SwiperSlide>
          <SwiperSlide>
            <div className="flex flex-col gap-3 items-center pb-4 pt-5">
              <ChanceDrop25 className="size-36" />
              <StringSmall className="max-w-[220px] text-center">
                {t('The chance of obtaining each reward is 25%.')}
              </StringSmall>
            </div>
          </SwiperSlide>
          <SwiperSlide>
            <div className="flex flex-col gap-4 items-center pb-4 pt-6">
              <Picture
                img={usdtCoinsPng}
                webp={usdtCoinsWebp}
                alt="usdt coins"
                className="h-[8.5rem] [&>*]:w-auto"
              />
              <StringSmall className="max-w-[220px] text-center">
                {t(
                  'Players can purchase the small and large packages separately.',
                )}
              </StringSmall>
            </div>
          </SwiperSlide>
        </Swiper>
        <AnimatePresence mode="wait">
          {activeSlide !== 2 ? (
            <MotionGreyButton
              key="grey"
              transition={{ duration: 0.2 }}
              initial={{ opacity: 0 }}
              exit={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              text=""
              onClick={() => swiperRef.current?.slideNext()}
              className="w-full mt-6"
              classNamesText="!text-white"
            >
              <motion.div
                {...(activeSlide !== 0
                  ? {
                      initial: { x: -40, opacity: 0 },
                      animate: { x: 0, opacity: 1 },
                    }
                  : {})}
                exit={{ x: -40, opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                <H2>{t('Next')}</H2>
              </motion.div>
            </MotionGreyButton>
          ) : (
            <MotionMainButton
              key="main"
              transition={{ duration: 0.2 }}
              initial={{ opacity: 0 }}
              exit={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              text=""
              onClick={onBuyOffer}
              className="w-full mt-6"
              classNamesText="!text-white"
            >
              <motion.div
                initial={{ x: 40, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 40, opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                <H2>{t('View rewards')}</H2>
              </motion.div>
            </MotionMainButton>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default ProfitDropInfoTab;
